// Lightweight stylised "first-page sample" thumbnail for the contracts
// library. Pure SVG/JSX — no images, no PDF rendering, fully themed.

import type { ContractMeta } from "@/lib/contract-templates";

type Props = {
  meta: ContractMeta;
  className?: string;
};

function titleLines(title: string): string[] {
  // Strip parenthetical suffixes like "(Free)" / "(Premium)" so the preview
  // shows the document title only.
  const cleaned = title
    .replace(/\(Free\)|\(Premium\)|—\s*Premium|—\s*Free/gi, "")
    .replace(/\s+/g, " ")
    .trim();
  const words = cleaned.split(" ");
  if (words.length <= 4) return [cleaned];
  const half = Math.ceil(words.length / 2);
  return [words.slice(0, half).join(" "), words.slice(half).join(" ")];
}

function layoutFor(category: ContractMeta["category"]):
  | "cv"
  | "letter"
  | "contract" {
  if (category === "Career") return "cv";
  if (category === "Debt" || category === "Compliance") return "letter";
  return "contract";
}

export function TemplatePreview({ meta, className }: Props) {
  const layout = layoutFor(meta.category);
  const tones = { accent: "#0F1B3D", chip: "#0F1B3D" };
  const lines = titleLines(meta.title);

  return (
    <div
      aria-hidden="true"
      className={`relative overflow-hidden rounded-md border border-border bg-white shadow-soft ${className ?? ""}`}
      style={{ aspectRatio: "1 / 1.414" }}
    >
      <svg
        viewBox="0 0 210 297"
        xmlns="http://www.w3.org/2000/svg"
        className="block h-full w-full"
        preserveAspectRatio="xMidYMid meet"
      >
        <rect x="0" y="0" width="210" height="297" fill="#ffffff" />
        <rect x="0" y="0" width="210" height="22" fill={tones.chip} />
        <rect x="14" y="8" width="22" height="6" rx="1" fill="#B08D57" />
        <rect x="40" y="9" width="60" height="2.4" rx="1" fill="#ffffff" opacity="0.95" />
        <rect x="40" y="13" width="36" height="1.6" rx="0.8" fill="#ffffff" opacity="0.6" />
        <rect x="160" y="9" width="36" height="2" rx="1" fill="#ffffff" opacity="0.7" />
        <rect x="170" y="13" width="26" height="1.6" rx="0.8" fill="#ffffff" opacity="0.5" />

        <rect x="14" y="30" width="36" height="6" rx="3" fill={tones.accent} opacity="0.12" />
        <text x="32" y="34.2" textAnchor="middle" fontSize="3.2" fontFamily="ui-sans-serif, system-ui" fontWeight="600" fill={tones.accent}>
          {meta.category.toUpperCase()}
        </text>

        {lines.map((ln, i) => (
          <text key={i} x="14" y={44 + i * 8} fontFamily="Georgia, serif" fontSize={lines.length > 1 ? 6.8 : 7.6} fontWeight="700" fill="#0F1B3D">
            {ln}
          </text>
        ))}
        <rect x="14" y={50 + (lines.length - 1) * 8} width="38" height="1" fill={tones.accent} />

        {layout === "cv" && <CvBody />}
        {layout === "letter" && <LetterBody />}
        {layout === "contract" && <ContractBody />}

        <rect x="0" y="282" width="210" height="15" fill="#F4F1EA" />
        <rect x="14" y="287" width="80" height="1.4" rx="0.7" fill="#0F1B3D" opacity="0.55" />
        <rect x="14" y="290.5" width="60" height="1.2" rx="0.6" fill="#0F1B3D" opacity="0.35" />
        <rect x="170" y="287" width="26" height="1.4" rx="0.7" fill={tones.accent} />

        <rect x="156" y="34" width="40" height="6.5" rx="3" fill="#10B981" />
        <text x="176" y="38.8" textAnchor="middle" fontSize="3.4" fontWeight="700" fill="#ffffff" fontFamily="ui-sans-serif, system-ui">
          FREE
        </text>
      </svg>
    </div>
  );
}

function CvBody() {
  const lineY = (i: number) => 75 + i * 4.2;
  return (
    <g>
      {/* Contact line */}
      <rect x="14" y="62" width="120" height="1.3" rx="0.6" fill="#0F1B3D" opacity="0.5" />
      <rect x="14" y="65.5" width="80" height="1.1" rx="0.5" fill="#0F1B3D" opacity="0.3" />

      <text x="14" y="74" fontFamily="ui-sans-serif" fontSize="3.6" fontWeight="700" fill="#0F1B3D">
        PROFESSIONAL PROFILE
      </text>
      {[0, 1, 2].map((i) => (
        <rect key={`p${i}`} x="14" y={lineY(i + 1)} width={170 - i * 14} height="1.2" rx="0.6" fill="#1e1e1e" opacity="0.35" />
      ))}

      <text x="14" y="100" fontFamily="ui-sans-serif" fontSize="3.6" fontWeight="700" fill="#0F1B3D">
        CORE SKILLS
      </text>
      {[0, 1, 2, 3].map((i) => (
        <g key={`s${i}`}>
          <circle cx="16" cy={106 + i * 5} r="0.8" fill="#B08D57" />
          <rect x="19" y={105.5 + i * 5} width={120 - (i % 2) * 18} height="1.2" rx="0.6" fill="#1e1e1e" opacity="0.5" />
        </g>
      ))}

      <text x="14" y="135" fontFamily="ui-sans-serif" fontSize="3.6" fontWeight="700" fill="#0F1B3D">
        WORK EXPERIENCE
      </text>
      <rect x="14" y="139" width="80" height="1.4" rx="0.7" fill="#0F1B3D" opacity="0.55" />
      <rect x="14" y="142.5" width="60" height="1" rx="0.5" fill="#0F1B3D" opacity="0.3" />
      {[0, 1, 2].map((i) => (
        <rect key={`e${i}`} x="14" y={147 + i * 3.5} width={170 - i * 10} height="1" rx="0.5" fill="#1e1e1e" opacity="0.3" />
      ))}

      <text x="14" y="170" fontFamily="ui-sans-serif" fontSize="3.6" fontWeight="700" fill="#0F1B3D">
        EDUCATION
      </text>
      <rect x="14" y="174" width="100" height="1.2" rx="0.6" fill="#0F1B3D" opacity="0.45" />
      <rect x="14" y="177.5" width="70" height="1" rx="0.5" fill="#0F1B3D" opacity="0.3" />
    </g>
  );
}

function LetterBody() {
  return (
    <g>
      <rect x="14" y="62" width="60" height="1.2" rx="0.6" fill="#0F1B3D" opacity="0.5" />
      <rect x="14" y="66" width="80" height="1.2" rx="0.6" fill="#0F1B3D" opacity="0.4" />
      <rect x="14" y="70" width="40" height="1.2" rx="0.6" fill="#0F1B3D" opacity="0.3" />

      <rect x="14" y="84" width="100" height="1.6" rx="0.8" fill="#0F1B3D" />

      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <rect
          key={i}
          x="14"
          y={94 + i * 4.5}
          width={i === 6 ? 100 : 182 - (i % 3) * 12}
          height="1.2"
          rx="0.6"
          fill="#1e1e1e"
          opacity="0.4"
        />
      ))}

      <rect x="14" y="146" width="100" height="1.4" rx="0.7" fill="#0F1B3D" opacity="0.6" />
      {[0, 1, 2, 3].map((i) => (
        <rect
          key={`p${i}`}
          x="14"
          y={154 + i * 4.5}
          width={182 - (i % 2) * 30}
          height="1.2"
          rx="0.6"
          fill="#1e1e1e"
          opacity="0.4"
        />
      ))}

      <rect x="14" y="218" width="50" height="1.4" rx="0.7" fill="#0F1B3D" opacity="0.5" />
      <rect x="14" y="240" width="60" height="1.6" rx="0.8" fill="#B08D57" opacity="0.7" />
      <rect x="14" y="244" width="40" height="1" rx="0.5" fill="#1e1e1e" opacity="0.4" />
    </g>
  );
}

function ContractBody() {
  return (
    <g>
      <rect x="14" y="62" width="100" height="1.4" rx="0.7" fill="#0F1B3D" opacity="0.55" />
      {[0, 1, 2].map((i) => (
        <g key={i}>
          <rect x="14" y={70 + i * 5} width="22" height="1.1" rx="0.55" fill="#0F1B3D" opacity="0.7" />
          <rect x="40" y={70 + i * 5} width={140 - i * 6} height="1.1" rx="0.55" fill="#1e1e1e" opacity="0.4" />
        </g>
      ))}

      <rect x="14" y="98" width="70" height="1.6" rx="0.8" fill="#0F1B3D" />
      {[0, 1, 2, 3, 4].map((i) => (
        <rect
          key={`b${i}`}
          x="14"
          y={106 + i * 4.5}
          width={182 - (i % 3) * 18}
          height="1.2"
          rx="0.6"
          fill="#1e1e1e"
          opacity="0.4"
        />
      ))}

      <rect x="14" y="138" width="70" height="1.6" rx="0.8" fill="#0F1B3D" />
      {[0, 1, 2, 3].map((i) => (
        <rect
          key={`b2${i}`}
          x="14"
          y={146 + i * 4.5}
          width={182 - (i % 2) * 22}
          height="1.2"
          rx="0.6"
          fill="#1e1e1e"
          opacity="0.4"
        />
      ))}

      {/* Signatures */}
      <line x1="20" y1="220" x2="90" y2="220" stroke="#0F1B3D" strokeWidth="0.3" />
      <line x1="120" y1="220" x2="190" y2="220" stroke="#0F1B3D" strokeWidth="0.3" />
      <text x="20" y="225" fontFamily="ui-sans-serif" fontSize="3" fill="#6c6c6c">
        Party A signature
      </text>
      <text x="120" y="225" fontFamily="ui-sans-serif" fontSize="3" fill="#6c6c6c">
        Party B signature
      </text>
    </g>
  );
}
